<template>
  <span v-if="false" aria-hidden="true"></span>
</template>

<script>
import {
  startBookingFunnelDelivery,
  stopBookingFunnelDelivery,
  trackBookingFunnelStep,
} from '@/services/bookingFunnelTracker';
import { bookingFunnelPageContext } from '@/services/bookingAttribution';
import { initializeBookingConversionTracking } from '@/services/googleAdsBookingConversion';

const STEP_CATALOG = {
  transportation: {
    1: { key: 'trip', label_en: 'Trip', label_es: 'Viaje' },
    2: { key: 'vehicle', label_en: 'Vehicle', label_es: 'Vehículo' },
    3: { key: 'extras', label_en: 'Extras', label_es: 'Extras' },
    4: { key: 'checkout', label_en: 'Checkout', label_es: 'Checkout' },
    5: { key: 'confirmation', label_en: 'Confirmation', label_es: 'Confirmación' },
  },
  tours: {
    1: { key: 'activities', label_en: 'Activities', label_es: 'Actividades' },
    2: { key: 'booking', label_en: 'Booking', label_es: 'Reserva' },
    3: { key: 'checkout', label_en: 'Checkout', label_es: 'Checkout' },
    4: { key: 'confirmation', label_en: 'Confirmation', label_es: 'Confirmación' },
  },
  restaurant: {
    1: { key: 'date_and_guests', label_en: 'Date and guests', label_es: 'Fecha y personas' },
    2: { key: 'contact', label_en: 'Contact', label_es: 'Contacto' },
    3: { key: 'confirmation', label_en: 'Confirmation', label_es: 'Confirmación' },
  },
};

const TRACKABLE_ROUTES = new Set([
  'booking-engine',
  'tours-booking-engine',
  'restaurant-booking-engine',
  'landing-page',
]);

export default {
  name: 'BookingFunnelTracker',
  data() {
    return {
      domObserver: null,
      domFrame: null,
      restaurantDomVersion: 0,
    };
  },
  computed: {
    company() {
      return this.$store.getters.getCompanyData?.data || null;
    },
    companyId() {
      return Number(this.company?.company_id || 0);
    },
    language() {
      return this.$store.getters.getBookingLanguage === 'es' ? 'es' : 'en';
    },
    activeServices() {
      const services = this.company?.company_active_services || [];
      if (Array.isArray(services)) return services.map(Number);

      if (typeof services === 'string') {
        try {
          const parsed = JSON.parse(services);
          return Array.isArray(parsed) ? parsed.map(Number) : [];
        } catch (error) {
          return services.split(',').map((service) => Number(service.trim())).filter(Boolean);
        }
      }

      return [];
    },
    industry() {
      if (!this.companyId || !TRACKABLE_ROUTES.has(this.$route.name)) return '';

      if (this.$route.name === 'landing-page') {
        return this.activeServices.includes(1) ? 'transportation' : '';
      }

      if (this.$route.name === 'restaurant-booking-engine') {
        return this.activeServices.includes(3) ? 'restaurant' : '';
      }
      if (this.$route.name === 'tours-booking-engine') {
        return this.activeServices.includes(2) ? 'tours' : '';
      }

      if (this.activeServices.includes(1)) return 'transportation';
      if (this.activeServices.includes(2)) return 'tours';
      if (this.activeServices.includes(3)) return 'restaurant';
      return '';
    },
    currentStoreStep() {
      return Number(this.$store.getters.getCurrentStep || this.$store.state.currentStep || 1);
    },
    currentStep() {
      if (!this.industry) return null;

      const catalog = STEP_CATALOG[this.industry];
      let stepNumber;
      if (this.industry === 'restaurant') {
        stepNumber = this.restaurantDomVersion >= 0 ? this.restaurantStepNumber() : null;
      } else {
        stepNumber = Math.max(1, Math.min(this.currentStoreStep, Object.keys(catalog).length));
      }

      if (!stepNumber || !catalog[stepNumber]) return null;
      const step = catalog[stepNumber];
      return {
        key: step.key,
        number: stepNumber,
        industry: this.industry,
        label: this.language === 'es' ? step.label_es : step.label_en,
        label_en: step.label_en,
        label_es: step.label_es,
      };
    },
    trackingSignature() {
      if (!this.companyId || !this.industry || !this.currentStep) return '';
      return `${this.companyId}|${this.industry}|${this.currentStep.key}`;
    },
  },
  watch: {
    trackingSignature: {
      immediate: true,
      handler() {
        this.trackCurrentStep();
      },
    },
    '$route.fullPath'() {
      this.queueDomRefresh();
      this.trackCurrentStep();
    },
  },
  mounted() {
    startBookingFunnelDelivery();
    this.observeBookingDom();
    this.queueDomRefresh();
    this.trackCurrentStep();
  },
  beforeUnmount() {
    stopBookingFunnelDelivery();
    this.domObserver?.disconnect();
    if (this.domFrame) window.cancelAnimationFrame(this.domFrame);
  },
  methods: {
    restaurantStepNumber() {
      if (document.querySelector('[data-restaurant-gift-card-flow]')) return null;
      if (document.querySelector('.restaurant-page .confirmation')) return 3;
      if (document.querySelector('.restaurant-page .hold-card, .restaurant-page .selected-slot-card')) return 2;
      return document.querySelector('.restaurant-page') ? 1 : null;
    },
    observeBookingDom() {
      if (!window.MutationObserver || !document.body) return;
      this.domObserver = new MutationObserver(this.queueDomRefresh);
      this.domObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'data-restaurant-gift-card-flow'],
      });
    },
    queueDomRefresh() {
      if (this.domFrame) return;
      this.domFrame = window.requestAnimationFrame(() => {
        this.domFrame = null;
        this.restaurantDomVersion += 1;
        this.trackCurrentStep();
      });
    },
    trackCurrentStep() {
      if (!this.companyId || !this.industry || !this.currentStep) return;
      trackBookingFunnelStep({
        companyId: this.companyId,
        industry: this.industry,
        bookingStep: this.currentStep,
        pageContext: bookingFunnelPageContext({ companyId: this.companyId, routeName: this.$route.name, landing: this.$store.getters.getLandingPageData, locale: this.language }),
      });
      if (this.industry === 'transportation') initializeBookingConversionTracking(this.companyId).catch(() => {});
    },
  },
};
</script>
